import React, { createContext, useContext } from "react";
import { checkIsArrayEmpty } from "repoV2/utils/common/dataTypes/array";
import { getDummyImageSrc } from "repoV2/utils/common/render/images";
import { checkIsEmptyHtmlString } from "repoV2/utils/common/validators/validators";
import { getPreviewableValue } from "repoV2/utils/previewPages";
import { useSalePageContext } from "../SalePageContext";
import { ADD_VIDEO_OPTIONS, DEFAULT_SOURCES, MEDIA_TYPE } from "../constants";

export type ICertificatesSectionContextValue = {
    hide: boolean;
    title: string;
    description: string;
    imgSrc: string;
    videoSrc: string;
    mediaType: string;
    pointersList: Array<{ title: string; description: string }>;
    hideControls: boolean;
    disableAutoPlay?: boolean | undefined;
    videoSrcType: string;
};

const initialContextValue: ICertificatesSectionContextValue = {
    hide: false,
    title: "",
    description: "",
    imgSrc: "",
    videoSrc: "",
    mediaType: "",
    pointersList: [],
    hideControls: false,
    disableAutoPlay: undefined,
    videoSrcType: ADD_VIDEO_OPTIONS.ADD_LINK.slug,
};

const CertificatesSectionContext = createContext(initialContextValue);

export function CertificatesSectionContextProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const { sectionsData, isPreview } = useSalePageContext();
    const pointersList =
        (isPreview &&
        checkIsArrayEmpty(sectionsData.certificatesSection.pointersList)
            ? [
                  {
                      title: "&lt;title 1&gt;",
                      description: "&lt;description 1&gt;",
                  },
                  {
                      title: "&lt;title 2&gt;",
                      description: "&lt;description 2&gt;",
                  },
                  {
                      title: "&lt;title 3&gt;",
                      description: "&lt;description 3&gt;",
                  },
              ]
            : sectionsData.certificatesSection.pointersList) || [];

    const title = getPreviewableValue({
        value: sectionsData.certificatesSection.title,
        dummyString: "&lt;Get certified&gt;",
        isPreview,
    });
    const description = getPreviewableValue({
        value: sectionsData.certificatesSection.description,
        dummyString:
            "&lt;Yes! You will be certified for this workshop once you submit your assignment.&gt;",
        isPreview,
    });
    const imgSrc =
        sectionsData.certificatesSection.imgSrc ||
        (isPreview ? getDummyImageSrc({ width: 700, height: 500 }) : "");
    const videoSrc =
        sectionsData.certificatesSection.videoSrc || DEFAULT_SOURCES.VIDEO;
    const mediaType =
        sectionsData.certificatesSection.mediaType || MEDIA_TYPE.IMAGE;
    const { disableAutoPlay } = sectionsData.certificatesSection;
    const hideControls = sectionsData.certificatesSection.hideControls || false;
    const contextValue: ICertificatesSectionContextValue = {
        hide:
            checkIsEmptyHtmlString(description) &&
            !imgSrc &&
            !videoSrc &&
            checkIsArrayEmpty(pointersList),

        title,
        description,
        imgSrc,
        videoSrc,
        mediaType,
        pointersList,
        hideControls,
        disableAutoPlay,
        videoSrcType:
            sectionsData.certificatesSection.videoSrcType ||
            initialContextValue.videoSrcType,
    };
    return (
        <CertificatesSectionContext.Provider value={contextValue}>
            {children}
        </CertificatesSectionContext.Provider>
    );
}

export function useCertificatesSectionContext() {
    return useContext(CertificatesSectionContext);
}
